import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, GraduationCap, MessageCircle, Star, Mail } from "lucide-react";
import { useConfig } from "@/context/ConfigContext";
import { assetUrl, formatINR } from "@/lib/api";
import ChatWidget from "@/components/ChatWidget";
import { About, Facilities, Gallery, Testimonials } from "@/components/site/Sections";
import EnquiryForm from "@/components/site/EnquiryForm";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/8a92fa15-bf2d-47d4-9a84-513604b4d8de/images/abd9c2832dfc7846382ff3fc3bccde00854b695c7fa17ee62036e779cedc0e90.jpeg";
const tel = (p) => `tel:${(p || "").replace(/[^\d+]/g, "")}`;

const Logo = ({ cfg, size = "w-11 h-11" }) => cfg.logo_url ? (
  <img src={assetUrl(cfg.logo_url)} alt={`${cfg.institute_name} logo`} className={`${size} rounded-xl object-contain bg-white`} data-testid="landing-logo" />
) : (
  <div className={`${size} rounded-xl bg-brand text-white flex items-center justify-center font-display font-semibold text-lg`} data-testid="landing-logo">
    {cfg.institute_name?.[0]}
  </div>
);

const NAV = [["about", "About"], ["classes", "Classes"], ["facilities", "Facilities"], ["gallery", "Gallery"], ["contact", "Contact"]];

const Header = ({ cfg, onEnquire }) => (
  <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <Logo cfg={cfg} />
        <span className="font-display font-semibold text-lg truncate" data-testid="landing-institute-name">{cfg.institute_name}</span>
      </div>
      <nav className="hidden md:flex items-center gap-6 text-sm text-stone-600">
        {NAV.map(([id, l]) => <a key={id} href={`#${id}`} className="hover:text-brand transition-colors" data-testid={`nav-${id}`}>{l}</a>)}
      </nav>
      <button onClick={onEnquire} className="px-4 py-2 rounded-full btn-accent text-sm font-semibold whitespace-nowrap" data-testid="header-enquire-button">
        Enquire now
      </button>
    </div>
  </header>
);

const Hero = ({ cfg, onEnquire }) => (
  <section className="relative overflow-hidden bg-brand-soft dots-bg">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-20 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
      <div>
        <span className="rise inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-soft text-stone-800 text-xs font-semibold mb-5">
          <Star className="w-3.5 h-3.5 text-accent-brand fill-current" /> Admissions open
        </span>
        <h1 className="rise rise-1 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" data-testid="landing-hero-title">
          {cfg.tagline || cfg.institute_name}
        </h1>
        <p className="rise rise-2 mt-5 text-base sm:text-lg text-stone-600 max-w-xl leading-relaxed" data-testid="landing-description">{cfg.description}</p>
        <div className="rise rise-3 mt-8 flex flex-wrap gap-3">
          <button onClick={onEnquire} className="flex items-center gap-2 px-6 py-3 rounded-full btn-brand font-semibold shadow-lg shadow-black/10" data-testid="landing-enquire-button">
            <MessageCircle className="w-5 h-5" /> Chat with {cfg.chatbot_name}
          </button>
          {cfg.phone && (
            <a href={tel(cfg.phone)} className="flex items-center gap-2 px-6 py-3 rounded-full bg-white border font-semibold hover:bg-stone-50 transition-colors" data-testid="landing-call-button">
              <Phone className="w-4 h-4 text-brand" /> {cfg.phone}
            </a>
          )}
        </div>
      </div>
      <div className="relative rise rise-2">
        <div className="absolute -top-5 -left-5 w-24 h-24 rounded-full bg-accent-brand opacity-80" />
        <div className="absolute -bottom-6 -right-4 w-32 h-32 rounded-[2rem] bg-brand opacity-90 rotate-12" />
        <img src={assetUrl(cfg.hero_image_url) || HERO_IMG} alt="Children learning together" data-testid="landing-hero-image"
          className="relative rounded-[2rem] w-full h-[300px] sm:h-[420px] object-cover shadow-xl" />
      </div>
    </div>
  </section>
);

const Classes = ({ cfg }) => (
  <section id="classes" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
    <p className="text-sm font-semibold text-brand uppercase tracking-wider">Our programmes</p>
    <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2 mb-4">Classes &amp; monthly fees</h2>
    {cfg.curriculum && <p className="text-stone-600 max-w-2xl mb-10" data-testid="landing-curriculum">{cfg.curriculum}</p>}
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
      {cfg.courses.map((c, i) => (
        <div key={c.name} data-testid="landing-program-card"
          className={`group rounded-3xl p-6 border transition-transform duration-300 hover:-translate-y-1 ${i % 3 === 0 ? "bg-brand text-white border-transparent" : "bg-white"}`}>
          <div className={`w-11 h-11 rounded-2xl flex items-center justify-center mb-6 ${i % 3 === 0 ? "bg-white/15" : "bg-brand-soft text-brand"}`}>
            <GraduationCap className="w-5 h-5" />
          </div>
          <h3 className="text-2xl font-semibold">{c.name}</h3>
          {c.age_group && <p className={`text-sm mt-1 ${i % 3 === 0 ? "text-white/75" : "text-stone-500"}`}>Age {c.age_group}</p>}
          <div className="mt-6 flex items-baseline gap-1" data-testid="landing-fee-card">
            <span className="font-display text-3xl font-semibold">{formatINR(c.monthly_fee)}</span>
            <span className={`text-sm ${i % 3 === 0 ? "text-white/75" : "text-stone-500"}`}>/ month</span>
          </div>
        </div>
      ))}
    </div>
  </section>
);

const Timings = ({ cfg }) => (
  <section id="timings" className="bg-accent-soft">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 grid md:grid-cols-[0.8fr_1.2fr] gap-8">
      <div>
        <p className="text-sm font-semibold text-brand uppercase tracking-wider">Schedule</p>
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight mt-2">Timings that fit your day</h2>
      </div>
      <ul className="space-y-3">
        {cfg.timings.map((t) => (
          <li key={t} className="flex items-center gap-4 bg-white rounded-2xl px-5 py-4 border" data-testid="landing-timing-item">
            <Clock className="w-5 h-5 text-brand shrink-0" />
            <span className="font-medium">{t}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const Contact = ({ cfg, onEnquire }) => (
  <section id="contact" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
    <div className="rounded-[2rem] bg-brand-dark text-white p-6 sm:p-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-10">
      <div>
        <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight">Visit us</h2>
        <p className="mt-3 text-white/75">Have a question about admissions? Fill the form or chat with our assistant.</p>
        <div className="space-y-5 mt-8">
          <div className="flex gap-4"><MapPin className="w-5 h-5 text-accent-brand shrink-0 mt-0.5" /><p data-testid="landing-address">{cfg.address}</p></div>
          {cfg.phone && <a href={tel(cfg.phone)} className="flex gap-4 hover:underline" data-testid="landing-phone"><Phone className="w-5 h-5 text-accent-brand shrink-0" />{cfg.phone}</a>}
          {cfg.email && <a href={`mailto:${cfg.email}`} className="flex gap-4 hover:underline" data-testid="landing-email"><Mail className="w-5 h-5 text-accent-brand shrink-0" />{cfg.email}</a>}
        </div>
        <button onClick={onEnquire} className="mt-8 px-6 py-3 rounded-full btn-accent font-semibold" data-testid="contact-enquire-button">Chat with {cfg.chatbot_name}</button>
      </div>
      <EnquiryForm />
    </div>
    <p className="text-center text-xs text-stone-400 mt-10">
      © {new Date().getFullYear()} {cfg.institute_name} · <Link to="/admin" className="hover:text-brand" data-testid="footer-admin-link">Admin login</Link>
    </p>
  </section>
);

export default function Landing() {
  const { config } = useConfig();
  const [chatOpen, setChatOpen] = useState(false);
  if (!config) return <div className="min-h-screen" />;
  const enquire = () => setChatOpen(true);
  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="school-site">
      <Header cfg={config} onEnquire={enquire} />
      <Hero cfg={config} onEnquire={enquire} />
      <About cfg={config} />
      <Classes cfg={config} />
      <Timings cfg={config} />
      <Facilities cfg={config} />
      <Gallery cfg={config} />
      <Testimonials cfg={config} />
      <Contact cfg={config} onEnquire={enquire} />
      <ChatWidget open={chatOpen} setOpen={setChatOpen} />
    </div>
  );
}
